/**
 * Pure (DOM-free) logic behind the Inventory item actions: what each action costs, what it would
 * produce, and why it is disabled. The screen renders these states verbatim, so every button
 * shows its cost, a preview and a human reason. Unit-tested in upgradeModel.test.ts.
 *
 * Since the Altar Forge (save v3) item level comes from crafting and tempering (temper.ts screen):
 * here the item card links there and offers Reforge (Fangsteel + gold), the Catalyst (turn a
 * tier-slot item into a set piece) and Salvage (resources back; replaces scrap). All checks defer
 * to progression's own functions so the UI can never disagree with the op.
 */
import type { ClassId } from '@keystone/sim';
import {
  type Gem,
  type Item,
  type OpError,
  type OpResult,
  type Profile,
  type RankTierDef,
  type ResourceBundle,
  type SecondaryStat,
  CATALYST_FANGSTEEL,
  MAX_ILVL,
  RANK_TIERS,
  REFORGE_FANGSTEEL,
  REFORGE_GOLD,
  SECONDARY_STATS,
  catalystCost,
  hasUnlock,
  isTierSlot,
  itemById,
  itemOwner,
  masterworkOdds,
  profileRating,
  profileStanding,
  reforge,
  revertReforge,
  salvageGems,
  salvageYield,
  shortfall,
  temperOdds,
  tierDef,
  tierPieceName,
  tierSetFor,
  upgradeCap,
} from '@keystone/progression';
import { STAT_LABEL } from '../ui/itemText';
import { shortfallText } from '../forge/resourceText';
import { formatPct } from './forgeModel';

/** A cost or gain (Fangsteel, Scales, gold, …). */
export type Cost = ResourceBundle;

/** One action's state: enabled + reason (null when enabled), cost and a preview of the result. */
export interface ActionState<P> {
  enabled: boolean;
  /** Why it can't be done right now (null when enabled). */
  reason: string | null;
  cost: Cost;
  preview: P | null;
}

const fmt = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** "Need 5 more Fangsteel and 100 more Gold." or null when affordable. */
export function affordReason(p: Profile, cost: Cost): string | null {
  return shortfallText(shortfall(p, cost));
}

// ---------------------------------------------------------------------------
// Tier cap
// ---------------------------------------------------------------------------

export interface TierCapInfo {
  /** Item level tempering stops at for the current tier. */
  cap: number;
  tier: RankTierDef;
  /** The next tier that raises the cap (null at the top). */
  next: RankTierDef | null;
}

export function tierCapInfo(p: Profile): TierCapInfo {
  const tier = tierDef(profileStanding(p).tier);
  const cap = upgradeCap(tier.maxIlvl);
  const next = RANK_TIERS.find((t) => t.minRating > tier.minRating && upgradeCap(t.maxIlvl) > cap) ?? null;
  return { cap, tier, next };
}

/** "At the Contender item level cap (220). Champion raises it to 240." */
export function capReason(info: TierCapInfo): string {
  if (info.cap >= MAX_ILVL) return `Already at the maximum item level (${MAX_ILVL}).`;
  const head = `At the ${info.tier.name} item level cap (${info.cap}).`;
  return info.next ? `${head} Reach ${info.next.name} (${fmt(info.next.minRating)} rating) to raise it to ${upgradeCap(info.next.maxIlvl)}.` : head;
}

// ---------------------------------------------------------------------------
// Temper / masterwork summary (the inventory card links to the Temper screen)
// ---------------------------------------------------------------------------

export interface TemperSummary {
  mode: 'temper' | 'masterwork' | 'maxed';
  /** "62% to reach 235" / "Masterwork +2: 55%" / reason. */
  text: string;
  chance: number | null;
  affordable: boolean;
}

export function temperSummary(p: Profile, item: Item): TemperSummary {
  const t = temperOdds(p, item);
  if (t.blocked !== 'capped') return { mode: 'temper', text: `${formatPct(t.odds.chance)} to reach item level ${t.target}${t.resonance ? ` · Resonance ${t.resonance}%` : ''}`, chance: t.odds.chance, affordable: !t.blocked };
  const m = masterworkOdds(p, item);
  if (m.blocked === 'maxMasterwork') return { mode: 'maxed', text: `${capReason(tierCapInfo(p))} Masterwork is at this rarity’s ceiling (+${m.ceiling}).`, chance: null, affordable: false };
  return { mode: 'masterwork', text: `At your cap. Masterwork +${m.step}: ${formatPct(m.odds.chance)}`, chance: m.odds.chance, affordable: !m.blocked };
}

// ---------------------------------------------------------------------------
// Reforge
// ---------------------------------------------------------------------------

export interface ReforgePreview {
  index: number;
  from: { stat: SecondaryStat; rating: number };
  to: { stat: SecondaryStat; rating: number };
}

const REFORGE_UNLOCK_TIER = RANK_TIERS.find((t) => t.unlocks.some((u) => u.unlock === 'reforge'));
export const REFORGE_PRICE: Cost = { fangsteel: REFORGE_FANGSTEEL, gold: REFORGE_GOLD };

/** Stats secondary `index` could become (not already on the item). */
export function reforgeChoices(item: Item, index: number): SecondaryStat[] {
  const cur = item.secondaries[index];
  if (!cur) return [];
  return SECONDARY_STATS.filter((s) => !item.secondaries.some((r) => r.stat === s));
}

/** Why reforging this item at all is blocked (unlock / already reforged), or null. */
export function reforgeBlocked(p: Profile, item: Item): string | null {
  if (!hasUnlock(profileRating(p), 'reforge')) return `Reforging unlocks at ${REFORGE_UNLOCK_TIER?.name ?? 'Contender'} (${fmt(REFORGE_UNLOCK_TIER?.minRating ?? 500)} rating).`;
  if (item.reforged) return `Already reforged (${STAT_LABEL[item.reforged.from]} → ${STAT_LABEL[item.secondaries[item.reforged.index]?.stat ?? item.reforged.from]}). Each item can be reforged once; revert it for free to choose again.`;
  return null;
}

export function reforgeAction(p: Profile, item: Item, index: number, to: SecondaryStat | null): ActionState<ReforgePreview> {
  const cost = REFORGE_PRICE;
  const blocked = reforgeBlocked(p, item);
  if (blocked) return { enabled: false, reason: blocked, cost, preview: null };
  const cur = item.secondaries[index];
  if (!cur) return { enabled: false, reason: 'Pick a secondary stat to swap.', cost, preview: null };
  if (!to) return { enabled: false, reason: `Pick a stat to replace ${STAT_LABEL[cur.stat]} with.`, cost, preview: null };
  const r = reforge(item, index, to);
  if (!r.ok) {
    const reason = r.reason === 'duplicateStat' ? `This item already has ${STAT_LABEL[to]}.` : r.reason === 'alreadyReforged' ? 'Already reforged.' : 'Pick a secondary stat to swap.';
    return { enabled: false, reason, cost, preview: null };
  }
  const preview = { index, from: { ...cur }, to: { stat: to, rating: cur.rating } };
  const afford = affordReason(p, cost);
  return { enabled: afford === null, reason: afford, cost, preview };
}

/**
 * Undo an item's reforge (free). Adapter: progression has `revertReforge(item)` but no profile op.
 */
export function revertReforgeItem(profile: Profile, itemId: string): OpResult {
  const it = itemById(profile, itemId);
  if (!it) return { ok: false, reason: 'unknownItem' };
  if (!it.reforged) return { ok: false, reason: 'badIndex' };
  const p = structuredClone(profile);
  const i = p.inventory.findIndex((x) => x.id === itemId);
  p.inventory[i] = revertReforge(p.inventory[i]!);
  return { ok: true, profile: p };
}

// ---------------------------------------------------------------------------
// Catalyst (make a tier piece)
// ---------------------------------------------------------------------------

export interface CatalystPreview {
  classId: ClassId;
  pieceName: string;
}

/** Turn `item` into `classId`'s tier piece (keeps everything else). */
export function catalystAction(p: Profile, item: Item, classId: ClassId): ActionState<CatalystPreview> {
  const cost = catalystCost();
  const set = tierSetFor(classId);
  const no = (reason: string): ActionState<CatalystPreview> => ({ enabled: false, reason, cost, preview: null });
  if (!set) return no('This hero has no tier set.');
  if (!isTierSlot(item.slot)) return no('Only helm, chest, legs, gloves and boots can be tier pieces.');
  if (item.legendaryId) return no('Legendaries can’t become tier pieces.');
  if (item.setId === set.id) return no('Already a tier piece.');
  const owner = itemOwner(p, item.id);
  if (owner && owner !== classId) return no('Another hero wears this item.');
  const preview = { classId, pieceName: tierPieceName(set.id, item.slot) ?? set.name };
  const afford = affordReason(p, cost);
  return { enabled: !afford, reason: afford, cost, preview };
}

export const CATALYST_BLURB = `1 Altar Sigil + ${CATALYST_FANGSTEEL} Fangsteel: becomes your tier piece, keeping its item level, rarity, stats, gems and temper progress.`;

// ---------------------------------------------------------------------------
// Salvage (replaces scrap)
// ---------------------------------------------------------------------------

export interface SalvagePreview {
  gained: ResourceBundle;
  gems: Gem[];
  /** Things worth a second look before confirming. */
  warnings: string[];
}

/** Salvage: resources back (Fangsteel, half the tempering Scales, its Sigil), gems to the stash. Blocked while worn. */
export function salvageAction(p: Profile, item: Item, nameOf: (classId: ClassId) => string = (c) => c): ActionState<SalvagePreview> {
  const gained = salvageYield(item);
  const gems = salvageGems(item);
  const warnings: string[] = [];
  if (item.rarity === 'legendary') warnings.push('This is a legendary.');
  if ((item.masterwork ?? 0) > 0) warnings.push(`Its ${item.masterwork} masterwork step${item.masterwork === 1 ? '' : 's'} will be lost.`);
  if (gems.length) warnings.push(`${gems.length} gem${gems.length === 1 ? '' : 's'} go${gems.length === 1 ? 'es' : ''} back to your stash.`);
  if (item.setId) warnings.push('Its Altar Sigil comes back to you.');
  const preview = { gained, gems, warnings };
  const cost: Cost = {};
  const owner = itemOwner(p, item.id);
  if (owner) return { enabled: false, reason: `Equipped by ${nameOf(owner)}. Unequip it first.`, cost, preview };
  return { enabled: true, reason: null, cost, preview };
}

// ---------------------------------------------------------------------------
// Errors from store.apply
// ---------------------------------------------------------------------------

const OP_ERROR_TEXT: Readonly<Record<OpError, string>> = {
  unknownItem: 'That item no longer exists.',
  unknownClass: 'Unknown hero.',
  wrongClass: 'That legendary belongs to another class.',
  legendaryLimit: 'You already wear the maximum number of legendaries.',
  equippedElsewhere: 'Another hero is wearing that item.',
  equipped: 'Unequip the item first.',
  locked: 'Not unlocked at your rank yet.',
  notEnoughShards: 'Not enough Shards.',
  notEnoughGold: 'Not enough gold.',
  notAtCap: 'Temper the item to your tier cap first.',
  maxMasterwork: 'Already at its masterwork ceiling.',
  capped: 'At your tier item level cap.',
  badTarget: 'Not a valid target.',
  alreadyReforged: 'Already reforged.',
  duplicateStat: 'The item already has that stat.',
  badIndex: 'Invalid choice.',
  badSocket: 'Invalid socket.',
  noGem: 'You have no gem of that kind.',
  notEnough: 'Not enough to do that.',
  maxRank: 'Already the highest rank.',
  rankLocked: 'That gem rank is locked at your tier.',
  // Altar Forge (progression forge.ts)
  cantAfford: 'Not enough resources.',
  notTierSlot: 'Tier pieces only go in helm, chest, legs, gloves or boots.',
  noLegendary: 'Your class has no legendary for that slot.',
  reagentConflict: 'A Sigil and a Serpent\'s Eye can\'t go into the same craft.',
  noToken: 'No Vault Forge Token to use.',
  badReagent: 'Those reagents can\'t be used here.',
  pendingRefine: 'Keep or discard the pending refine first.',
  noRefine: 'Nothing to keep or discard.',
  isLegendary: 'Legendaries can\'t become tier pieces.',
  alreadyTier: 'Already a tier piece.',
  notReady: 'Not ready yet.',
  maxSockets: 'No more sockets fit on this item.',
  maxGilding: 'Already fully gilded.',
  notTierPiece: 'Only tier set pieces can be reshaped with a Sigil.',
  maxAttunement: 'Your next Eye is already guaranteed Legendary.',
};

export function opErrorText(e: OpError): string {
  return OP_ERROR_TEXT[e] ?? 'That did not work.';
}
